/**
 * Panel — die Grundfläche des Designs „Comic Arena".
 *
 * Tiefe entsteht hier NICHT mehr durch Leuchten, sondern durch zwei Dinge:
 * eine 2 px starke Ink-Outline und einen harten Offset-Schatten ohne Weichzeichner.
 * Wer hier einen `blur`-Schatten oder ein `shadow-glow` ergänzt, bricht die
 * Designsprache — der einzige erlaubte Schimmer ist `.holo`, und der ist
 * ausschließlich für Boss und Legendär reserviert.
 *
 * Der `tone` färbt die Outline und trägt damit die Bedeutung: Ein rot
 * umrandetes Panel IST ein Gegner, ein pink umrandetes eine Elite. Deshalb nie
 * dekorativ wählen.
 *
 * Spezifikation: docs/design-system/components/core/Panel.jsx
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type PanelTone = "default" | "secondary" | "primary" | "enemy" | "elite" | "boss";

/** Outline-Farbe je Bedeutung. Die Fläche bleibt bis auf den Boss immer `card`. */
const TONE_BORDER: Record<PanelTone, string> = {
  default: "border-ink",
  secondary: "border-ink",
  primary: "border-accent",
  enemy: "border-destructive",
  elite: "border-elite",
  boss: "border-boss",
};

const TONE_BG: Record<PanelTone, string> = {
  default: "bg-card",
  secondary: "bg-secondary",
  primary: "bg-card",
  enemy: "bg-card",
  elite: "bg-card",
  // Der Boss ist die einzige Ausnahme: wärmerer Grund, damit Gold darauf trägt.
  boss: "bg-[oklch(0.34_0.07_60)]",
};

export function Panel({
  tone = "default",
  grid = false,
  holo = false,
  className,
  contentClassName,
  children,
}: {
  tone?: PanelTone;
  /** Halftone-Raster über der Fläche — Textur, nie Dekoration. */
  grid?: boolean;
  /** Nur für Boss und Legendär. */
  holo?: boolean;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border-2 shadow-hard",
        TONE_BORDER[tone],
        TONE_BG[tone],
        holo && "holo",
        className,
      )}
    >
      {grid ? (
        <div aria-hidden className="halftone pointer-events-none absolute inset-0 opacity-70" />
      ) : null}
      <div className={cn("relative z-[1] p-3", contentClassName)}>{children}</div>
    </div>
  );
}
